// The phone's local store (IndexedDB through Dexie): the outbox of changes made without signal
// and the cache of data already synced (REQ-OFF-02, REQ-OFF-03).
import Dexie, { type Table } from "dexie";

export type OutboxOp = {
  operation_id: string;
  action: string;
  payload: unknown;
  recorded_at: string;
  status: "waiting" | "sending" | "rejected" | "held";
  message?: string | undefined;
  temp_ref?: string | undefined;
  attempts: number;
};

/**
 * A synced row kept for offline use. `read_at` is when the read that brought it was asked for (milliseconds): a refresh
 * never replaces a row that a newer read brought while the refresh was downloading (AT-006). Not indexed, so no upgrade.
 */
export type CacheRow = { key: string; table: string; id: string; data: Record<string, unknown>; updated_at?: string; read_at?: number };
/**
 * A photo, voice note or document kept on the phone until it is uploaded. Its id is the phone's id for the file
 * (client_file_id, Decision 84 #1). A row with uploaded false and no failed_reason is waiting.
 * Decision 84 #1 round 2 (F4 a): the bytes are kept in their own table (db.fileBytes, same id), so counting, listing and
 * summarising the files never loads them. `blob` is the older shape (rows written before version 3, which kept the
 * bytes in the row: a Blob before 29 Oct 2026, an ArrayBuffer since); such a row is still a file like any other.
 */
export type FileBlob = { id: string; operation_id: string; filename: string; mime: string; source: string; uploaded: boolean; doc_type?: string; transcript?: string; upload_batch?: string;
  /** The older shape: the bytes in the row itself (see above). New rows keep them in db.fileBytes. */
  blob?: Blob | ArrayBuffer | undefined;
  /** The file's size in bytes (rows since version 3). */
  size?: number;
  /** The server refused it (4xx): its message, word for word. It is not sent again until the person retries it. */
  failed_reason?: string | undefined;
  /** The server already had the same file (a duplicate arrival). */
  duplicate?: boolean;
  /** The server was told it was picked (document.intend). */
  announced?: boolean };
/**
 * A waiting file's bytes (Decision 84 #1 round 2, F4 a), read only to upload that one file. An ArrayBuffer read the
 * moment the file was picked (WebKit can refuse to store a Blob, and a picked file can become unreadable once the page
 * is in the background); a Blob only when moved from an older row. The file's name travels with its bytes.
 */
export type FileBytes = { id: string; filename: string; data: Blob | ArrayBuffer };
/**
 * One file picked in the Inbox, recorded BEFORE any file is saved (Decision 84 #1), so a file picked but never kept
 * (the page closed or the phone locked part-way) is still named at the next open. Its id is the file's db.files id.
 */
export type Pick = { id: string; selection: string; upload_batch?: string; name: string; size: number; type: string; source: string; picked_at: string;
  /** Its place in the selection (the files upload in the order picked). */
  order?: number;
  /** The page (one open of the app) that picked it: a pick left unkept by another page was never kept. */
  page: string;
  /** Set when the file could not be kept on this phone. */
  not_kept?: string;
  announced?: boolean;
  /** The person pressed OK on the start-up notice naming this pick as never kept (Decision 84 #1 round 2, F3). */
  notice_ok?: boolean };
export type Meta = { key: string; value: unknown };

const isBuffer = (v: unknown): v is ArrayBuffer => Object.prototype.toString.call(v) === "[object ArrayBuffer]";

class RkDb extends Dexie {
  outbox!: Table<OutboxOp, string>;
  cache!: Table<CacheRow, string>;
  files!: Table<FileBlob, string>;
  meta!: Table<Meta, string>;
  picks!: Table<Pick, string>;
  fileBytes!: Table<FileBytes, string>;
  constructor() {
    super("rk-platform");
    this.version(1).stores({
      outbox: "operation_id, status, recorded_at",
      cache: "key, table, id",
      files: "id, operation_id, uploaded",
      meta: "key"
    });
    // Decision 84 #1: the files picked in the Inbox, recorded before they are saved.
    this.version(2).stores({ picks: "id, selection, picked_at" });
    // Decision 84 #1 round 2 (F4 a): a waiting file's bytes move to their own table, so the counts and lists that re-run on
    // every write never load them. Lossless (F4 c): each row with bytes held as an ArrayBuffer is moved one at a time,
    // in this upgrade's own transaction; a row whose move fails keeps its bytes where they are and is read from there
    // (a Blob row stays as it is: reading it back loads a handle, not the bytes).
    this.version(3).stores({ fileBytes: "id" }).upgrade(async (tx) => {
      const files = tx.table<FileBlob, string>("files");
      const bytes = tx.table<FileBytes, string>("fileBytes");
      for (const id of await files.toCollection().primaryKeys()) {
        try {
          const row = await files.get(id);
          const data = row?.blob;
          if (!row || !isBuffer(data)) continue;
          const meta: FileBlob = { ...row, size: data.byteLength };
          delete meta.blob;
          if (!row.uploaded) await bytes.put({ id, filename: row.filename, data });
          await files.put(meta);
        } catch (e) {
          console.warn(`A waiting file's bytes were left in place: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
    });
  }
}

export const db = new RkDb();

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return (row?.value as T) ?? fallback;
}
export async function setMeta(key: string, value: unknown) {
  await db.meta.put({ key, value });
}
